/**
 * PrecipitationAnimationWebGLLayer.tsx
 * غلاف React لطبقة أنيميشن الهطول (PrecipAnimGLLayer) — تُدرَج فوق طبقة لون الأمطار.
 *
 * تحلّ محلّ PrecipitationParticleLayer (جسيمات Canvas). راجع رأس PrecipAnimGLLayer.ts
 * لسبب الانتقال إلى شيدر: هطول Zoom Earth ليس جسيمات أصلاً بل دالّتان إجرائيّتان داخل
 * شيدر الجزء، وهو ما يجعل استحالة الخروج خارج منطقة الهطول والثباتَ على الأرض وثباتَ
 * الكثافة خصائصَ بنيوية لا معايرةً يدوية.
 */

import { useEffect, useRef } from 'react';
import { useMapRef } from './MapContext';
import { PrecipAnimGLLayer } from './webgl/PrecipAnimGLLayer';
import { getWeatherInsertBeforeId, raiseParticlesAboveWeatherLayers, PRECIP_ANIM_LAYER_ID } from './webgl/layerOrder';

interface Props {
    /** يجب أن يساوي PRECIP_ANIM_LAYER_ID — عليه يعتمد ترتيب المكدّس في layerOrder. */
    id: typeof PRECIP_ANIM_LAYER_ID;
    enabled: boolean;
    /** نسيج تراكم الأمطار (٣ ساعات) — نفس النسيج الذي يُلوَّن به الحقل، فيتطابق الاثنان. */
    precipUrl: string | null;
    /** نسيج الحرارة — يفصل المطر عن الثلج. بدونه يُعرض الكل مطراً (لا نخترع ثلجاً). */
    tempUrl: string;
    /** نسيج o-index — يخفَّف بياض المطر فوق النشاط الرعدي كي تبقى الصواعق مقروءة. */
    stormUrl: string;
}

export function PrecipitationAnimationWebGLLayer({ id, enabled, precipUrl, tempUrl, stormUrl }: Props) {
    const mapRef = useMapRef();
    const layerRef = useRef<PrecipAnimGLLayer | null>(null);
    // أحدث الروابط عبر refs: الإضافة تتأخّر حتى جهوز الستايل، فقد تتغيّر الروابط قبلها.
    const urls = useRef({ precipUrl, tempUrl, stormUrl });
    urls.current = { precipUrl, tempUrl, stormUrl };

    useEffect(() => {
        const wrap = mapRef.current;
        if (!wrap || !enabled) return;
        const map = wrap.getMap();
        let cancelled = false;
        const layer = new PrecipAnimGLLayer(id);

        const add = () => {
            if (cancelled) return;
            try {
                layer.setUrl('precip', urls.current.precipUrl ?? '');
                layer.setUrl('temp', urls.current.tempUrl);
                layer.setUrl('storm', urls.current.stormUrl);
                if (!map.getLayer(id)) map.addLayer(layer, getWeatherInsertBeforeId(map));
                // يُعيد بناء المكدّس كاملاً: الأنيميشن فوق حقل اللون وتمريرة البحر،
                // وتحت جسيمات الرياح وتسميات الخريطة.
                raiseParticlesAboveWeatherLayers(map);
                layerRef.current = layer;
            } catch (e) { console.error(`PrecipitationAnimationWebGLLayer(${id})`, e); }
        };

        let intervalId: number | undefined;
        const onStyleData = () => { if (map.isStyleLoaded()) { cleanupWait(); add(); } };
        const cleanupWait = () => {
            map.off('styledata', onStyleData);
            if (intervalId !== undefined) window.clearInterval(intervalId);
        };
        if (map.isStyleLoaded()) add();
        else {
            map.on('styledata', onStyleData);
            intervalId = window.setInterval(() => { if (map.isStyleLoaded()) { cleanupWait(); add(); } }, 150);
        }

        return () => {
            cancelled = true;
            cleanupWait();
            layerRef.current = null;
            try { if (map.getLayer(id)) map.removeLayer(id); } catch { /* تجاهل */ }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapRef, id, enabled]);

    useEffect(() => { layerRef.current?.setUrl('precip', precipUrl ?? ''); }, [precipUrl]);
    useEffect(() => { layerRef.current?.setUrl('temp', tempUrl); }, [tempUrl]);
    useEffect(() => { layerRef.current?.setUrl('storm', stormUrl); }, [stormUrl]);

    return null;
}
